import React, { useState, useEffect } from 'react';
import { approvalService } from 'api/services/approvalService';
import type { ApprovalHistory } from 'api/types/approval.types';
import { formatDate } from 'utils/lmmsHelpers';

interface ReturnedHistorySectionProps {
  recordType: 'addon_request' | 'closing' | 'combined' | 'gl_replacement';
  recordId: string;
}

export const ReturnedHistorySection: React.FC<ReturnedHistorySectionProps> = ({
  recordType,
  recordId,
}) => {
  const [entries, setEntries] = useState<ApprovalHistory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!recordId) return;
    setLoading(true);
    approvalService.getHistory(recordType, recordId)
      .then((response) => {
        const returned = (response.data ?? []).filter(
          (h) => h.action?.toLowerCase() === 'returned'
        );
        setEntries(returned);
      })
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [recordType, recordId]);

  if (loading) {
    return (
      <div style={{ padding: '0.75rem', color: 'var(--text-muted)', fontSize: '0.8rem', fontStyle: 'italic' }}>
        Loading workflow history...
      </div>
    );
  }

  return (
    <div style={{ marginTop: '1.5rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
      <h3 style={{
        fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)',
        margin: '0 0 0.75rem 0', borderBottom: '2px solid var(--primary)', paddingBottom: '0.5rem',
      }}>
        Workflow Information
      </h3>
      {entries.length > 0 ? (
        <div style={{ marginBottom: '1rem' }}>
          <label className="form-label" style={{ marginBottom: '0.5rem' }}>Returned History</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {entries.map((entry, idx) => (
              <div key={idx} style={{
                padding: '0.625rem 0.875rem',
                background: 'rgba(245, 158, 11, 0.06)',
                border: '1px solid rgba(245, 158, 11, 0.2)',
                borderRadius: '0.375rem',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                  <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#92400e' }}>
                    Returned by {entry.actionedByName}
                  </span>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                    {formatDate(entry.actionedAt)}
                  </span>
                </div>
                {entry.remark && (
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    {entry.remark}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div style={{ padding: '0.75rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', marginBottom: '1rem' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>No returned history</span>
        </div>
      )}
    </div>
  );
};
